// BRIEF 91 §3a — THE FIVE TIMER PROPS, BUILT ONCE, FOR EITHER PARENT KIND.
//
// ── WHY THIS FILE EXISTS, AND IT IS NOT "TO SHARE CODE" ─────────────────────
//
// Twice now a wrapper around `SubtaskChecklist` has been found passing PART of
// what the component needs, and both times the missing part was invisible from
// the wrapper's own file:
//
//   brief 90 §3a  `EventSubtasks` passed no `co` / `currentUser` /
//                 `assignable` / `canAssignAll`, so an event subtask's assignee
//                 could not be set and an existing one rendered as nothing.
//   brief 91 §3a  the same wrapper passed no `onLogTime` / `onStartTimer` /
//                 `isTimerRunning` / `runningSubtaskId` / `subtaskTimers`, so
//                 an event subtask could not be timed or logged.
//
// Neither was a typo. `SubtaskChecklist`'s props are an untyped bag (`: any`),
// so EVERY subset compiles, and the five timer props are five separate optional
// names with a derivation each — four of the five are computed FROM
// `activeTimers` by expressions that lived only inside `CaseTaskRow`. A wrapper
// author who has not read that file cannot know they exist, and nothing tells
// them.
//
// THE ANSWER IS THAT THE GROUP BECOMES ONE VALUE WITH A REQUIRED SHAPE. A host
// calls `subtaskTiming({...})` and spreads the result; every argument below is
// REQUIRED, so a host that has not been given `activeTimers`, or `startTimer`,
// or `openModal` fails `tsc` at the call site rather than rendering a row with
// a missing button. The derivations are written once here instead of copied,
// which is the actual root cause: they were never copied at all.
//
// WHAT THIS STILL DOES NOT CATCH, said plainly because a net that oversells
// itself is worse than a smaller one: a wrapper that never calls this function
// AT ALL still compiles, exactly as `EventSubtasks` did for two briefs. What is
// now impossible is the PARTIAL omission — three of five, four of five — and
// that is what both 90 §3a and 91 §3a actually were.

type SubtaskLike = { id?: number | null; text?: string | null };

export type TimerRowShape = {
  taskId?: number | null;
  subtaskId?: number | null;
  eventId?: number | null;
  paused?: boolean;
  elapsed?: number;
  startedAt?: number;
};

/** Exactly the props `SubtaskChecklist` reads for its timer column. */
export type SubtaskTiming = {
  onLogTime: (s: SubtaskLike) => void;
  onStartTimer: (s: SubtaskLike) => void;
  isTimerRunning: boolean;
  runningSubtaskId: number | null;
  subtaskTimers: TimerRowShape[];
};

export type SubtaskTimingArgs = {
  /** Which parent owns these subtasks. Decides how a timer row is keyed and
   *  whether the time entry may claim a task. */
  kind: "task" | "event";
  parentId: number;
  /** The matter the entry binds to — the parent's own, both kinds. */
  caseId: number | null;
  ccId: number | null;
  /** Activity type seeded into the time entry (the parent's). */
  type: string | null;
  /** Who is logging. */
  currentUser: number | null;
  /** The host's live timer list. */
  activeTimers: readonly TimerRowShape[] | null | undefined;
  startTimer: (taskId?: unknown, subtaskId?: unknown, opts?: unknown) => void;
  openModal: (kind: unknown, seed?: unknown) => void;
};

export function subtaskTiming({
  kind,
  parentId,
  caseId,
  ccId,
  type,
  currentUser,
  activeTimers,
  startTimer,
  openModal,
}: SubtaskTimingArgs): SubtaskTiming {
  const isEvent = kind === "event";
  const rows = Array.isArray(activeTimers) ? activeTimers : [];
  // A subtask timer of THIS parent. The two kinds key on different fields
  // because an event subtask's timer has no task to name (`timerSubject.ts`).
  const mine = rows.filter(
    (at) =>
      at &&
      at.subtaskId != null &&
      (isEvent ? at.eventId === parentId : at.taskId === parentId),
  );
  return {
    onLogTime: (st: SubtaskLike) =>
      openModal("te", {
        caId: caseId || "",
        ccId: ccId || "",
        coId: currentUser || "",
        date: new Date().toISOString().slice(0, 10),
        type: type || "",
        /* Phase 29 Stage-3 C19 — subtask text only, no parent-title prefix
           (the case/parent ride their own fields). */
        desc: st.text || "",
        billed: false,
        min: 0,
        /* An event subtask has no task, and `_fromTask: true` would be a
           "there is one, I cannot name it" claim the server would act on
           (timeEntries.js resolves it, then adopts that task's case). The
           entry binds through `subtaskId`, which is parent-agnostic. */
        ...(isEvent ? {} : { _fromTask: parentId }),
        _fromSubtask: 1,
        subtaskId: st.id,
      }),
    onStartTimer: (st: SubtaskLike) =>
      isEvent
        ? startTimer(null, st.id, { eventId: parentId })
        : startTimer(parentId, st.id),
    /* The legacy boolean pair, kept because `SubtaskChecklist` still falls back
       to it for hosts that pass no `subtaskTimers` (its C92 note). */
    isTimerRunning: mine.length > 0,
    runningSubtaskId: mine.length ? (mine[0].subtaskId ?? null) : null,
    subtaskTimers: mine,
  };
}
